import React, {useEffect, useRef, useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {
  DayItem,
  DayItems,
  DayItemText,
  Divider,
  HeaderTitle,
  ObjectiveText,
  ObjectiveTitle,
  SidenavBox,
  SidenavExpandedContent,
  SidenavToggleBox,
  StyledIcon,
} from '../AssetsSidenav/styles'

import SidenavHideIcon from '../../../images/icons/new-dashboard/sidenav-hide-icon.svg'
import SidenavExpandIcon from '../../../images/icons/new-dashboard/sidenav-expand-icon.svg'
import VideoIcon from '../../../images/icons/new-dashboard/video-icon.svg'
import VideoIconActive from '../../../images/icons/new-dashboard/live-video.svg'
import {LinkBox} from '../../../styles/global'
import {WorkshopReplayGroup} from '../../../redux/workshopReplay'
import {formatSeconds} from '../../../utils/hoursMinutes'

type Props = {
  workshopReplayId: string
  group?: WorkshopReplayGroup
  isExpanded: boolean
  onChangeIsExpanded: (value: boolean) => void
}

const View = ({
  workshopReplayId,
  group,
  isExpanded,
  onChangeIsExpanded,
}: Props) => {
  const [height, setHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const [hoverStates, setHoverStates] = useState<boolean[]>([])

  const handleMouseEnter = (index: number) => {
    setHoverStates(
      prev => prev?.map((hover, i) => (i === index ? true : hover)),
    )
  }

  const handleMouseLeave = (index: number) => {
    setHoverStates(
      prev => prev?.map((hover, i) => (i === index ? false : hover)),
    )
  }

  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight)
    }
  }, [group])

  const formatWeekRange = (startDate?: string) => {
    if (startDate) {
      const start = new Date(startDate)
      start.setHours(start.getHours() + 12)

      const options = {month: 'short', day: 'numeric'} as const

      const startFormatted = start.toLocaleDateString('en-US', options)

      return `Week of: ${startFormatted}`
    }
  }

  const customOrder = ['PcM', 'PjM', 'PA', 'PPD', 'PDD', 'CE']

  const sortedData = [...(group?.replays ?? [])]
    .sort((a, b) => {
      return customOrder.indexOf(a.abbr) - customOrder.indexOf(b.abbr)
    })
    .filter(g => g.brightcoveId)

  useEffect(() => {
    if (sortedData && sortedData.length !== hoverStates.length) {
      setHoverStates(Array(sortedData.length).fill(false))
    }
  }, [sortedData])

  const getIcon = (hover: boolean, isActive: boolean) => {
    if (isActive || hover) {
      return <StyledIcon src={VideoIconActive} alt="Active Video" />
    }

    return <StyledIcon src={VideoIcon} alt="Video" />
  }

  return (
    <SidenavBox $isExpanded={isExpanded} height={height} ref={contentRef}>
      <SidenavToggleBox
        onClick={() => onChangeIsExpanded(!isExpanded)}
        $isExpanded={isExpanded}
      >
        <img
          src={isExpanded ? SidenavHideIcon : SidenavExpandIcon}
          alt="Toggle Menu"
        />
        {isExpanded && <span>Hide menu</span>}
      </SidenavToggleBox>
      <SidenavExpandedContent>
        <HeaderTitle>Virtual Workshops</HeaderTitle>
        <Divider />
        <ObjectiveTitle>Replays:</ObjectiveTitle>
        <ObjectiveText>
          <span>{formatWeekRange(group?.startDate)}</span>
        </ObjectiveText>
        <DayItems>
          {sortedData.map((replay, key) => {
            const isActive = replay.id === workshopReplayId

            return (
              <React.Fragment key={`replay-item-${key}`}>
                <Divider />
                <LinkBox
                  component={RouterLink}
                  to={`/workshops/replay/${replay.id}`}
                >
                  <DayItem
                    $isActive={isActive}
                    onMouseEnter={() => handleMouseEnter(key)}
                    onMouseLeave={() => handleMouseLeave(key)}
                  >
                    {getIcon(hoverStates[key], isActive)}
                    <div>
                      <DayItemText $isLarge={true}>{replay.title}</DayItemText>
                      <DayItemText>
                        {formatSeconds(replay.videoDuration)}
                      </DayItemText>
                    </div>
                  </DayItem>
                </LinkBox>
              </React.Fragment>
            )
          })}
        </DayItems>
      </SidenavExpandedContent>
    </SidenavBox>
  )
}

export default View
